import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";
import {adminService} from "../services/admin.service";
import {
    ActionResponse,
    ActiveTask,
    AdminFile,
    AdminLink,
    AdminPhotoOrder,
    AdminPreOrder,
    AdminUser,
    FailedTask,
    PaginatedResponse,
    QueueInfo,
    TaskStats,
    WorkerInfo,
} from "../interfaces/admin";


export const adminApi = createApi({
    reducerPath: "adminApi",
    baseQuery: fetchBaseQuery({baseUrl: "/"}),
    tagTypes: ["AdminUsers", "AdminLinks", "AdminFiles", "AdminPreOrders", "AdminPhotofixation", "AdminTasks"],
    endpoints: (builder) => ({
        // Tasks
        getTaskStats: builder.query<TaskStats, void>({
            queryFn: async () => {
                try {
                    const response = await adminService.getTaskStats();
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminTasks"],
        }),
        getFailedTasks: builder.query<FailedTask[], void>({
            queryFn: async () => {
                try {
                    const response = await adminService.getFailedTasks();
                    return {data: response.data?.items ?? response.data ?? []};
                } catch (e: any) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminTasks"],
        }),
        getActiveTasks: builder.query<ActiveTask[], void>({
            queryFn: async () => {
                try {
                    const response = await adminService.getActiveTasks();
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminTasks"],
        }),
        getReservedTasks: builder.query<ActiveTask[], void>({
            queryFn: async () => {
                try {
                    const response = await adminService.getReservedTasks();
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminTasks"],
        }),
        getQueues: builder.query<QueueInfo[], void>({
            queryFn: async () => {
                try {
                    const response = await adminService.getQueues();
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminTasks"],
        }),
        getWorkers: builder.query<WorkerInfo[], void>({
            queryFn: async () => {
                try {
                    const response = await adminService.getWorkers();
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminTasks"],
        }),
        retryTask: builder.mutation<ActionResponse, number>({
            queryFn: async (taskId) => {
                try {
                    const response = await adminService.retryTask(taskId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminTasks"],
        }),
        revokeTask: builder.mutation<ActionResponse, string>({
            queryFn: async (taskId) => {
                try {
                    const response = await adminService.revokeTask(taskId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminTasks"],
        }),
        purgeQueue: builder.mutation<ActionResponse, string>({
            queryFn: async (queueName) => {
                try {
                    const response = await adminService.purgeQueue(queueName);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminTasks"],
        }),
        pauseWorkers: builder.mutation<ActionResponse, void>({
            queryFn: async () => {
                try {
                    const response = await adminService.pauseWorkers();
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminTasks"],
        }),
        resumeWorkers: builder.mutation<ActionResponse, void>({
            queryFn: async () => {
                try {
                    const response = await adminService.resumeWorkers();
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminTasks"],
        }),

        // Users
        getAdminUsers: builder.query<PaginatedResponse<AdminUser>, Record<string, any> | void>({
            queryFn: async (params) => {
                try {
                    const response = await adminService.getUsers(params || undefined);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminUsers"],
            keepUnusedDataFor: 0,
        }),
        getAdminUser: builder.query<AdminUser, number>({
            queryFn: async (userId) => {
                try {
                    const response = await adminService.getUser(userId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminUsers"],
        }),
        createAdminUser: builder.mutation<AdminUser, {username: string; password: string; is_admin: boolean}>({
            queryFn: async (data) => {
                try {
                    const response = await adminService.createUser(data);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminUsers"],
        }),
        updateAdminUser: builder.mutation<AdminUser, {userId: number; data: {username?: string; password?: string; is_admin?: boolean}}>({
            queryFn: async ({userId, data}) => {
                try {
                    const response = await adminService.updateUser(userId, data);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminUsers"],
        }),
        deleteAdminUser: builder.mutation<ActionResponse, number>({
            queryFn: async (userId) => {
                try {
                    const response = await adminService.deleteUser(userId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminUsers"],
        }),

        // Links
        getAdminLinks: builder.query<PaginatedResponse<AdminLink>, Record<string, any> | void>({
            queryFn: async (params) => {
                try {
                    const response = await adminService.getLinks(params || undefined);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminLinks"],
            keepUnusedDataFor: 0,
        }),
        getAdminLink: builder.query<AdminLink, number>({
            queryFn: async (linkId) => {
                try {
                    const response = await adminService.getLink(linkId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminLinks"],
        }),
        updateAdminLink: builder.mutation<AdminLink, {linkId: number; data: Record<string, any>}>({
            queryFn: async ({linkId, data}) => {
                try {
                    const response = await adminService.updateLink(linkId, data);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminLinks"],
        }),
        deleteAdminLink: builder.mutation<ActionResponse, number>({
            queryFn: async (linkId) => {
                try {
                    const response = await adminService.deleteLink(linkId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminLinks"],
        }),

        // Files
        getAdminFiles: builder.query<PaginatedResponse<AdminFile>, Record<string, any> | void>({
            queryFn: async (params) => {
                try {
                    const response = await adminService.getFiles(params || undefined);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminFiles"],
            keepUnusedDataFor: 0,
        }),
        getAdminFile: builder.query<AdminFile, number>({
            queryFn: async (fileId) => {
                try {
                    const response = await adminService.getFile(fileId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminFiles"],
        }),
        retryAdminFile: builder.mutation<ActionResponse, number>({
            queryFn: async (fileId) => {
                try {
                    const response = await adminService.retryFile(fileId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminFiles"],
        }),
        deleteAdminFile: builder.mutation<ActionResponse, number>({
            queryFn: async (fileId) => {
                try {
                    const response = await adminService.deleteFile(fileId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminFiles"],
        }),

        // Pre-Orders
        getAdminPreOrders: builder.query<PaginatedResponse<AdminPreOrder>, Record<string, any> | void>({
            queryFn: async (params) => {
                try {
                    const response = await adminService.getPreOrders(params || undefined);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminPreOrders"],
            keepUnusedDataFor: 0,
        }),
        getAdminPreOrder: builder.query<AdminPreOrder & {links?: AdminLink[]}, number>({
            queryFn: async (preOrderId) => {
                try {
                    const response = await adminService.getPreOrder(preOrderId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminPreOrders"],
        }),
        createAdminPreOrder: builder.mutation<AdminPreOrder, {name: string}>({
            queryFn: async (data) => {
                try {
                    const response = await adminService.createPreOrder(data);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminPreOrders"],
        }),
        updateAdminPreOrder: builder.mutation<AdminPreOrder, {preOrderId: number; data: {name?: string}}>({
            queryFn: async ({preOrderId, data}) => {
                try {
                    const response = await adminService.updatePreOrder(preOrderId, data);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminPreOrders"],
        }),
        deleteAdminPreOrder: builder.mutation<ActionResponse, number>({
            queryFn: async (preOrderId) => {
                try {
                    const response = await adminService.deletePreOrder(preOrderId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminPreOrders"],
        }),
        attachAdminLink: builder.mutation<ActionResponse, {preOrderId: number; linkId: number}>({
            queryFn: async ({preOrderId, linkId}) => {
                try {
                    const response = await adminService.attachLink(preOrderId, linkId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminPreOrders"],
        }),
        detachAdminLink: builder.mutation<ActionResponse, {preOrderId: number; linkId: number}>({
            queryFn: async ({preOrderId, linkId}) => {
                try {
                    const response = await adminService.detachLink(preOrderId, linkId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminPreOrders"],
        }),

        // Photofixation
        getAdminPhotoOrders: builder.query<PaginatedResponse<AdminPhotoOrder>, Record<string, any> | void>({
            queryFn: async (params) => {
                try {
                    const response = await adminService.getPhotoOrders(params || undefined);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminPhotofixation"],
            keepUnusedDataFor: 0,
        }),
        getAdminPhotoOrder: builder.query<AdminPhotoOrder, number>({
            queryFn: async (orderId) => {
                try {
                    const response = await adminService.getPhotoOrder(orderId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            providesTags: ["AdminPhotofixation"],
        }),
        retryAdminPhotoFile: builder.mutation<ActionResponse, number>({
            queryFn: async (fileId) => {
                try {
                    const response = await adminService.retryPhotoFile(fileId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminPhotofixation"],
        }),
        deleteAdminPhotoOrder: builder.mutation<ActionResponse, number>({
            queryFn: async (orderId) => {
                try {
                    const response = await adminService.deletePhotoOrder(orderId);
                    return {data: response.data};
                } catch (e) {
                    return {error: e.message};
                }
            },
            invalidatesTags: ["AdminPhotofixation"],
        }),
    }),
});

export const {
    // Tasks
    useGetTaskStatsQuery,
    useGetFailedTasksQuery,
    useGetActiveTasksQuery,
    useGetReservedTasksQuery,
    useGetQueuesQuery,
    useGetWorkersQuery,
    useRetryTaskMutation,
    useRevokeTaskMutation,
    usePurgeQueueMutation,
    usePauseWorkersMutation,
    useResumeWorkersMutation,
    // Users
    useGetAdminUsersQuery,
    useGetAdminUserQuery,
    useCreateAdminUserMutation,
    useUpdateAdminUserMutation,
    useDeleteAdminUserMutation,
    // Links
    useGetAdminLinksQuery,
    useGetAdminLinkQuery,
    useUpdateAdminLinkMutation,
    useDeleteAdminLinkMutation,
    // Files
    useGetAdminFilesQuery,
    useGetAdminFileQuery,
    useRetryAdminFileMutation,
    useDeleteAdminFileMutation,
    // Pre-Orders
    useGetAdminPreOrdersQuery,
    useGetAdminPreOrderQuery,
    useCreateAdminPreOrderMutation,
    useUpdateAdminPreOrderMutation,
    useDeleteAdminPreOrderMutation,
    useAttachAdminLinkMutation,
    useDetachAdminLinkMutation,
    // Photofixation
    useGetAdminPhotoOrdersQuery,
    useGetAdminPhotoOrderQuery,
    useRetryAdminPhotoFileMutation,
    useDeleteAdminPhotoOrderMutation,
} = adminApi;
